
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import SimpleInputMixin from "@/mixins/SimpleInputMixin.vue";
import FormDialog from "@/components/dialogs/FormDialog.vue";
import DokumanForm from "@/components/forms/DokumanForm.vue";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import { FileEntity } from "@/entity/FileEntity";
import { DokumanTuruLabel } from "@/enum/DokumanTuru";

@Component({ components: { DeleteDialog, DokumanForm, FormDialog } })
export default class DokumanList extends Mixins(SimpleInputMixin) {
  /** Örn: /api/v1/muvekkil/123 (GET için /file eklenir) */
  @Prop() action!: string;
  /** Tür listesinin çekileceği endpoint */
  @Prop() turAction!: string;
  @Prop() height!: string;
  @Prop({ default: true }) autoFetch!: boolean;

  @Prop({ default: () => ({ "items-per-page-options": [3, 5, 10] }) })
  footerProps!: any;

  DokumanTuruLabel = DokumanTuruLabel;

  headers = [
    { text: "Tür", value: "dokuman_turu_id", align: "start" },
    { text: "Açıklama", value: "aciklama", align: "start", sortable: false },
    { text: "Dosya Adı", value: "name", align: "start", sortable: false },
    { text: "İşlemler", value: "actions", width: 100, align: "end", sortable: false }
  ];

  /** v-model her zaman Array olsun */
  get rows(): FileEntity[] {
    const v = (this as any).localValue;
    return Array.isArray(v) ? v : [];
  }

  isTableLoading = false;

  get tableItems(): FileEntity[] {
    const src = this.rows || [];
    return src.map((v: any, idx: number) => ({
      ...v,
      _k: v?.id ?? v?.file_id ?? v?.file_path ?? `row-${idx}`
    }));
  }

  mounted() { if (this.autoFetch) this.fetchList(); }
  @Watch("action") onActionChange() { if (this.autoFetch) this.fetchList(); }

  private normalizeList(payload: any): FileEntity[] {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.files)) return payload.files;
    if (Array.isArray(payload?.rows)) return payload.rows;
    return [];
  }

  async fetchList() {
    if (!this.action) {
      this.$emit("input", []);
      return;
    }
    this.isTableLoading = true;
    try {
      const { data } = await this.$http.get(`${this.action}/files`);
      const list = this.normalizeList(data);
      this.$emit("input", list);
    } catch (e) {
      console.error("DokumanList fetchList error", e);
      this.$emit("input", []);
    } finally {
      this.isTableLoading = false;
    }
  }

  // --------- helpers ---------
  fileId(item: any)     { return item?.id ?? item?.file_id ?? item?.fileId ?? item?._id ?? null; }
  filePath(item: any)   { return item?.file_path ?? item?.path ?? item?.file ?? null; }
  fileDeletable(item: any)    { return this.fileId(item) != null; }
  fileDownloadable(item: any) { return !!(this.filePath(item) || this.fileId(item)); }

  fileName(item: any): string {
    if (item?.name && item.name !== "blob") return String(item.name);
    if (item?.original_name) return String(item.original_name);
    if (item?.file_name) return String(item.file_name);
    const p = this.filePath(item);
    return p ? String(p).split("/").pop() || "dosya" : "dosya";
  }

  typeLabel(item: any): string {
    const id  = Number(item?.dokuman_turu_id ?? item?.type_id);
    const obj = item?.dokuman_turu?.isim;
    if (id === 10 || (obj && obj.toLowerCase?.() === "diğer")) return "Diğer";
    return this.DokumanTuruLabel?.[id] || obj || "Diğer";
  }

  deleteUrl(item: any): string {
    const id = this.fileId(item);
    return id != null ? `/api/v1/file/${id}` : "";
  }

  download(item: any) {
    if (!this.fileDownloadable(item)) return;
    const label = this.fileName(item);
    const p = this.filePath(item);
    if (p) {
      const clean = String(p).replace(/^\/?uploads\//, "");
      return this.$helper.download(label, `/api/v1/file/download/${clean}`);
    }
    const id = this.fileId(item);
    if (id != null) {
      return this.$helper.download(label, `/api/v1/file/${id}/download`);
    }
  }

  private refreshTimer: any = null;

  async load() {
    if (this.autoFetch) {
      await this.fetchList();
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.fetchList(), 1200);
    }
    this.$emit("load");
  }

  beforeDestroy() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
  }

  isAuthorized(keySuffix: string) {
    const ap = [...(this.$store.state.activeProcesses || [])].reverse();
    if (!ap.length || !ap[0] || !ap[0].key) return false;
    if (keySuffix[0] !== "-") keySuffix = "-" + keySuffix;

    let authKey: string | null = null;
    if (ap[0].key === "takip-duzenle" || ap[0].key === "takip-ac") authKey = "takip-dokuman" + keySuffix;
    else if (ap[0].key === "borclu-detay")  authKey = "borclu-dokuman" + keySuffix;
    else if (ap[0].key === "alacakli-detay")authKey = "muvekkil-dokuman" + keySuffix;
    else if (ap[0].key === "vekalet-detay") authKey = "vekalet-dokuman" + keySuffix;
    else if (ap[0].key === "muvekkil-detay")authKey = "muvekkil-dokuman" + keySuffix;

    return !!authKey && this.$helper.isAuthorized(authKey);
  }
}
